import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { companyService, Company } from '../services/companyService';
import { useUserStore } from '../store/userStore';
import CompanyFlowBuilder from '../components/CompanyFlowBuilder';
import FlowBuilderModal from '../components/FlowBuilderModal';
import { KnowledgeBaseUploadModal } from '../components/KnowledgeBaseUploadModal';
import CustomSelect from '../components/CustomSelect';
import { useToast } from '../contexts/ToastContext';
import { useProgress } from '../components/TopProgressBar';
import Spinner from '../components/Spinner';
import Button from '../components/Button';

interface FlowStep {
  stepId: string;
  fieldName: string;
  question: string;
  type: 'text' | 'email' | 'phone' | 'number' | 'select';
  validation: {
    required: boolean;
    type: string;
  };
  errorMessage?: string;
  options?: Array<{
    id: string;
    label: string;
    value: string;
  }>;
  dependsOn?: string;
  showWhen?: string;
}

const CompaniesPage: React.FC = () => {
  const { user, canAccessCompany } = useUserStore();
  const toast = useToast();
  const { start: progressStart, done: progressDone, showLoader, hideLoader } = useProgress();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [editingCompany, setEditingCompany] = useState<Partial<Company> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'flow'>('info');
  const [flowSteps, setFlowSteps] = useState<FlowStep[]>([]);
  const [showFlowModal, setShowFlowModal] = useState(false);
  const [showKnowledgeBaseModal, setShowKnowledgeBaseModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCompany, setNewCompany] = useState<Partial<Company>>({
    agentActive: true,
    estado: 'active',
    prompt: '',
    urlWebHook: '',
  });

  // Mobile: controla si se muestra la lista o el detalle
  const [showDetailInMobile, setShowDetailInMobile] = useState(false);

  // ── Panel izquierdo redimensionable ───────────────────────────────────────
  const AGENTS_MIN     = 200;
  const AGENTS_MAX     = 400;
  const AGENTS_DEFAULT = 288; // w-72 = 18rem = 288px

  const [agentsPanelWidth, setAgentsPanelWidth] = useState(() => {
    const saved = localStorage.getItem('agents_panel_width');
    if (saved) {
      const n = parseInt(saved, 10);
      if (!isNaN(n) && n >= AGENTS_MIN && n <= AGENTS_MAX) return n;
    }
    return AGENTS_DEFAULT;
  });

  const agentsDragging       = useRef(false);
  const agentsDragStartX     = useRef(0);
  const agentsDragStartWidth = useRef(0);

  const onAgentsMouseMove = useCallback((e: MouseEvent) => {
    if (!agentsDragging.current) return;
    const delta = e.clientX - agentsDragStartX.current;
    const next  = Math.min(AGENTS_MAX, Math.max(AGENTS_MIN, agentsDragStartWidth.current + delta));
    setAgentsPanelWidth(next);
  }, []);

  const onAgentsMouseUp = useCallback(() => {
    if (!agentsDragging.current) return;
    agentsDragging.current = false;
    document.body.style.cursor    = '';
    document.body.style.userSelect = '';
    setAgentsPanelWidth(prev => {
      localStorage.setItem('agents_panel_width', String(prev));
      return prev;
    });
    window.removeEventListener('mousemove', onAgentsMouseMove);
    window.removeEventListener('mouseup', onAgentsMouseUp);
  }, [onAgentsMouseMove]);

  const onAgentsHandleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    agentsDragging.current       = true;
    agentsDragStartX.current     = e.clientX;
    agentsDragStartWidth.current = agentsPanelWidth;
    document.body.style.cursor    = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onAgentsMouseMove);
    window.addEventListener('mouseup', onAgentsMouseUp);
  };

  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      progressStart();
      const data = await companyService.getCompanies();
      
      // Filtrar empresas según permisos del usuario
      let filteredCompanies = data;
      if (user && user.role !== 'administrador') {
        filteredCompanies = data.filter(company => 
          canAccessCompany(company.configId)
        );
      }
      
      setCompanies(filteredCompanies);
    } catch (err) {
      setError('Error al cargar los agentes');
      toast.error('Error al cargar los agentes');
      console.error(err);
    } finally {
      setLoading(false);
      progressDone();
      hideLoader();
    }
  }, [user, canAccessCompany]);

  // Loader inmediato al montar
  useLayoutEffect(() => {
    showLoader('Cargando agentes...');
    progressStart();
    return () => { hideLoader(); progressDone(); };
  }, []); // eslint-disable-line

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const handleSelectCompany = (company: Company) => {
    setSelectedCompany(company);
    setEditingCompany({ ...company });
    setFlowSteps((company as any).flujoBot || []);
    setActiveTab('info');
    setError(null);
    setShowDetailInMobile(true); // en mobile navegar al detalle
  };

  const handleInputChange = (field: keyof Company, value: any) => {
    if (editingCompany) {
      setEditingCompany({
        ...editingCompany,
        [field]: value,
      });
    }
  };

  const handleAddStep = (step: FlowStep) => {
    setFlowSteps([...flowSteps, step]);
  };

  const handleUpdateStep = (stepId: string, updatedStep: FlowStep) => {
    setFlowSteps(flowSteps.map(s => s.stepId === stepId ? updatedStep : s));
  };

  const handleDeleteStep = (stepId: string) => {
    setFlowSteps(flowSteps.filter(s => s.stepId !== stepId));
  };

  const handleSave = async () => {
    if (!selectedCompany || !editingCompany) return;

    try {
      setLoading(true);
      setError(null);

      // Procesar pineconeNamespaces: convertir string a array si contiene comas
      let processedNamespaces = (editingCompany as any).pineconeNamespaces;
      if (typeof processedNamespaces === 'string' && processedNamespaces.trim()) {
        if (processedNamespaces.includes(',')) {
          processedNamespaces = processedNamespaces.split(',').map(ns => ns.trim()).filter(ns => ns);
        }
      } else if (processedNamespaces === '') {
        processedNamespaces = undefined;
      }

      // Preparar datos para actualizar (excluir campos no editables)
      const updateData: Partial<Company> = {
        nombreEmpresa: editingCompany.nombreEmpresa,
        accessToken: editingCompany.accessToken,
        agentActive: editingCompany.agentActive,
        estado: editingCompany.estado,
        prompt: editingCompany.prompt,
        urlWebHook: editingCompany.urlWebHook,
        phoneNumberId: editingCompany.phoneNumberId,
        wspNumberId: editingCompany.wspNumberId,
        accountId: editingCompany.accountId,
        pineconeNamespaces: processedNamespaces,
        pineconeApiKey: (editingCompany as any).pineconeApiKey,
        mensajeDefault: (editingCompany as any).mensajeDefault,
        ...(activeTab === 'flow' && { flujoBot: flowSteps as any }),
      };

      const updated = await companyService.updateCompany(selectedCompany.configId, updateData);
      
      // Actualizar la lista
      setCompanies(prev =>
        prev.map(c => c.configId === updated.configId ? updated : c)
      );
      
      setSelectedCompany(updated);
      setEditingCompany(updated);
      setFlowSteps((updated as any).flujoBot || []);
      toast.success('Agente actualizado');
    } catch (err) {
      setError('Error al actualizar la empresa');
      toast.error('Error al actualizar el agente');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (selectedCompany) {
      setEditingCompany({ ...selectedCompany });
      setFlowSteps((selectedCompany as any).flujoBot || []);
    }
  };

  const handleCreateCompany = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validar campos requeridos
      if (!newCompany.configId || !newCompany.nombreEmpresa || !newCompany.phoneNumberId || 
          !newCompany.wspNumberId || !newCompany.accessToken || !newCompany.accountId) {
        setError('Todos los campos marcados con * son requeridos');
        setLoading(false);
        return;
      }

      const created = await companyService.createCompany(newCompany as Omit<Company, 'fechaCreacion' | 'fechaActualizacion'>);
      
      // Agregar a la lista
      setCompanies(prev => [...prev, created]);
      
      // Seleccionar la nueva empresa
      setSelectedCompany(created);
      setEditingCompany(created);
      setFlowSteps([]);
      
      // Cerrar modal y limpiar formulario
      setShowCreateModal(false);
      setNewCompany({
        agentActive: true,
        estado: 'active',
        prompt: '',
        urlWebHook: '',
      });
      
      toast.success('Agente creado');
    } catch (err: any) {
      if (err.response?.status === 409) {
        setError('Ya existe un agente con ese ID');
        toast.error('Ya existe un agente con ese ID');
      } else {
        setError('Error al crear el agente');
        toast.error('Error al crear el agente');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-full bg-brand-bg">
      {/* ── Panel izquierdo: lista de agentes ─────────────────────── */}
      <div
        className={`${showDetailInMobile ? 'hidden' : 'flex'} md:flex bg-brand-surface flex-col border-r border-brand-border flex-shrink-0 relative w-full md:w-auto`}
        style={window.innerWidth >= 768 ? { width: agentsPanelWidth, minWidth: agentsPanelWidth, maxWidth: agentsPanelWidth } : {}}
      >
        {/* Header */}
        <div className="px-5 py-5 border-b border-brand-border">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-base font-bold text-brand-dark">Agentes</h1>
              <p className="text-xs text-brand-mid mt-0.5">Gestión de agentes</p>
            </div>
            {user?.role === 'administrador' && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowCreateModal(true)}
                title="Crear nuevo agente"
              >
                + Nuevo
              </Button>
            )}
          </div>
        </div>

        {/* Lista */}
        <div className="flex-1 overflow-y-auto py-2">
          {loading && companies.length === 0 ? (
            <div className="p-6 text-center text-brand-mid text-sm">Cargando...</div>
          ) : companies.length === 0 ? (
            <div className="p-8 text-center text-brand-mid">
              <svg className="w-10 h-10 mx-auto mb-2 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
              </svg>
              <p className="text-sm">No hay agentes</p>
            </div>
          ) : (
            companies.map((company) => {
              const isSelected = selectedCompany?.configId === company.configId;
              return (
                <button
                  key={company.configId}
                  onClick={() => handleSelectCompany(company)}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-all border-l-[3px]
                    ${isSelected
                      ? 'bg-brand-yellow/10 border-brand-yellow'
                      : 'hover:bg-brand-light border-transparent'
                    }`}
                >
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm flex-shrink-0
                    ${isSelected ? 'bg-brand-dark text-brand-yellow' : 'bg-brand-mid/20 text-brand-dark'}`}>
                    {company.nombreEmpresa.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-brand-dark text-sm truncate">{company.nombreEmpresa}</p>
                    <p className="text-[10px] text-brand-mid truncate font-mono">{company.phoneNumberId}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0 ${
                    company.estado === 'active'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-600'
                  }`}>
                    {company.estado === 'active' ? 'Activa' : 'Inactiva'}
                  </span>
                </button>
              );
            })
          )}
        </div>

        {/* Handle de resize */}
        <div
          onMouseDown={onAgentsHandleMouseDown}
          title="Arrastra para redimensionar"
          className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize group z-10"
        >
          <div className="absolute inset-y-0 right-0 w-px bg-transparent group-hover:bg-brand-yellow/60 transition-colors duration-150" />
          <div className="absolute top-1/2 -translate-y-1/2 right-0 translate-x-1/2 w-3 h-8 rounded-full bg-gray-200 group-hover:bg-brand-yellow/80 transition-colors duration-150 opacity-0 group-hover:opacity-100" />
        </div>
      </div>

      {/* ── Panel derecho: detalle del agente ──────────────────────── */}
      <div className={`${showDetailInMobile ? 'flex' : 'hidden md:flex'} flex-1 flex-col bg-brand-bg overflow-hidden w-full md:w-auto`}>
        {selectedCompany && editingCompany ? (
          <>
            {/* Header */}
            <div className="bg-brand-surface border-b border-brand-border px-4 md:px-8 py-4 md:py-5 flex items-center gap-3 shadow-sm">
              {/* Volver en mobile */}
              <button onClick={() => setShowDetailInMobile(false)}
                className="md:hidden p-1.5 hover:bg-brand-light rounded-lg transition text-brand-mid flex-shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="flex-1 min-w-0">
                <h2 className="text-base md:text-lg font-bold text-brand-dark truncate">{selectedCompany.nombreEmpresa}</h2>
                <p className="text-xs text-brand-mid font-mono mt-0.5 truncate">ID: {selectedCompany.configId}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0 ${
                selectedCompany.estado === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
              }`}>
                {selectedCompany.estado === 'active' ? 'Activa' : 'Inactiva'}
              </span>
            </div>

            {/* Tabs — en mobile íconos + texto corto, sin wrap */}
            <div className="flex border-b border-brand-border bg-brand-surface overflow-x-auto scrollbar-none">
              <button
                onClick={() => setActiveTab('info')}
                className={`px-3 md:px-6 py-3 font-semibold text-xs md:text-sm whitespace-nowrap transition border-b-2 flex-shrink-0 ${
                  activeTab === 'info'
                    ? 'text-brand-dark border-brand-yellow'
                    : 'text-brand-mid border-transparent hover:text-brand-dark'
                }`}
              >
                Información
              </button>
              <button
                onClick={() => setActiveTab('flow')}
                className={`px-3 md:px-6 py-3 font-semibold text-xs md:text-sm whitespace-nowrap transition border-b-2 flex-shrink-0 ${
                  activeTab === 'flow'
                    ? 'text-brand-dark border-brand-yellow'
                    : 'text-brand-mid border-transparent hover:text-brand-dark'
                }`}
              >
                Flujo Bot
              </button>
              <button
                onClick={() => setShowFlowModal(true)}
                className="hidden md:block px-3 md:px-6 py-3 font-semibold text-xs md:text-sm whitespace-nowrap text-brand-mid hover:text-brand-dark transition ml-auto border-b-2 border-transparent flex-shrink-0"
                title="Abrir editor de flujo en pantalla completa"
              >
                <span className="hidden sm:inline">Pantalla Completa</span>
                <span className="sm:hidden">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                </span>
              </button>
              <button
                onClick={() => setShowKnowledgeBaseModal(true)}
                className="px-3 md:px-6 py-3 font-semibold text-xs md:text-sm whitespace-nowrap text-brand-mid hover:text-brand-dark transition border-b-2 border-transparent flex-shrink-0"
                title="Subir base de conocimiento desde Excel"
              >
                <span className="hidden sm:inline">Subir Excel</span>
                <span className="sm:hidden">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                </span>
              </button>
            </div>

            {/* Mensajes de estado — solo errores inline, success via toast */}
            {error && (
              <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Contenido */}
            <div className={`flex-1 overflow-hidden ${activeTab === 'info' ? 'overflow-y-auto p-4 md:p-8' : 'p-0'}`}>
              {activeTab === 'info' ? (
                <div className="max-w-2xl space-y-5">

                  {/* helper para label+input */}
                  {[
                    { label: 'Nombre de la Empresa', field: 'nombreEmpresa', type: 'text', placeholder: '' },
                    { label: 'URL WebHook', field: 'urlWebHook', type: 'text', placeholder: '' },
                    { label: 'Phone Number ID', field: 'phoneNumberId', type: 'text', placeholder: '' },
                    { label: 'WSP Number ID', field: 'wspNumberId', type: 'text', placeholder: '' },
                    { label: 'Account ID', field: 'accountId', type: 'text', placeholder: '' },
                  ].map(({ label, field, type }) => (
                    <div key={field}>
                      <label className="block text-xs font-semibold text-brand-dark mb-1.5">{label}</label>
                      <input
                        type={type}
                        value={(editingCompany as any)[field] || ''}
                        onChange={(e) => handleInputChange(field as any, e.target.value)}
                        className="w-full px-4 py-2.5 bg-brand-surface border border-brand-border rounded-xl text-sm text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                      />
                    </div>
                  ))}

                  {/* Access Token */}
                  <div>
                    <label className="block text-xs font-semibold text-brand-dark mb-1.5">Access Token</label>
                    <textarea
                      value={editingCompany.accessToken || ''}
                      onChange={(e) => handleInputChange('accessToken', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2.5 bg-brand-surface border border-brand-border rounded-xl text-xs text-brand-dark font-mono focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                    />
                  </div>

                  {/* Prompt */}
                  <div>
                    <label className="block text-xs font-semibold text-brand-dark mb-1.5">Prompt del Agente</label>
                    <textarea
                      value={editingCompany.prompt || ''}
                      onChange={(e) => handleInputChange('prompt', e.target.value)}
                      rows={5}
                      className="w-full px-4 py-2.5 bg-brand-surface border border-brand-border rounded-xl text-sm text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                    />
                  </div>

                  {/* Mensaje Default */}
                  <div>
                    <label className="block text-xs font-semibold text-brand-dark mb-1.5">
                      Mensaje por Defecto
                      <span className="text-brand-mid font-normal ml-1">(cuando el agente está desactivado)</span>
                    </label>
                    <textarea
                      value={(editingCompany as any).mensajeDefault || ''}
                      onChange={(e) => handleInputChange('mensajeDefault' as any, e.target.value)}
                      rows={3}
                      placeholder="Gracias por tu información. Un agente se comunicará contigo pronto."
                      className="w-full px-4 py-2.5 bg-brand-surface border border-brand-border rounded-xl text-sm text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                    />
                  </div>

                  {/* Pinecone Namespaces */}
                  <div>
                    <label className="block text-xs font-semibold text-brand-dark mb-1.5">
                      Namespaces de Pinecone
                      <span className="text-brand-mid font-normal ml-1">(separados por comas)</span>
                    </label>
                    <input
                      type="text"
                      value={
                        Array.isArray((editingCompany as any).pineconeNamespaces)
                          ? (editingCompany as any).pineconeNamespaces.join(', ')
                          : (editingCompany as any).pineconeNamespaces || ''
                      }
                      onChange={(e) => handleInputChange('pineconeNamespaces' as any, e.target.value)}
                      placeholder="default o empresa-abc, productos-generales"
                      className="w-full px-4 py-2.5 bg-brand-surface border border-brand-border rounded-xl text-sm text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                    />
                    <p className="text-xs text-brand-mid mt-1">Deja vacío para usar "default". Usa comas para múltiples namespaces.</p>
                  </div>

                  {/* Pinecone API Key */}
                  <div>
                    <label className="block text-xs font-semibold text-brand-dark mb-1.5">
                      Pinecone API Key
                      <span className="text-brand-mid font-normal ml-1">(opcional)</span>
                    </label>
                    <input
                      type="text"
                      value={(editingCompany as any).pineconeApiKey || ''}
                      onChange={(e) => handleInputChange('pineconeApiKey' as any, e.target.value)}
                      placeholder="pcsk_..."
                      className="w-full px-4 py-2.5 bg-brand-surface border border-brand-border rounded-xl text-sm text-brand-dark font-mono focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                    />
                  </div>

                  {/* Agente Activo + Estado — filas separadas para consistencia */}
                  <div>
                    <label className="flex items-center gap-2.5 cursor-pointer bg-brand-surface border border-brand-border px-4 py-2.5 rounded-xl w-full">
                      <input
                        type="checkbox"
                        checked={editingCompany.agentActive || false}
                        onChange={(e) => handleInputChange('agentActive', e.target.checked)}
                        className="w-4 h-4 rounded focus:ring-brand-yellow accent-brand-dark"
                      />
                      <span className="text-sm font-semibold text-brand-dark">Agente Activo</span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-brand-dark mb-1.5">Estado</label>
                    <CustomSelect
                      value={editingCompany.estado || 'active'}
                      onChange={(val) => handleInputChange('estado', val as 'active' | 'inactive')}
                      options={[
                        { value: 'active', label: 'Activa' },
                        { value: 'inactive', label: 'Inactiva' },
                      ]}
                    />
                  </div>

                  {/* Read-only */}
                  <div className="bg-brand-light rounded-xl px-4 py-4 space-y-3">
                    <p className="text-xs font-bold text-brand-dark">Solo lectura</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-semibold text-brand-mid mb-1">Fecha de Creación</label>
                        <input
                          type="text"
                          value={new Date(selectedCompany.fechaCreacion).toLocaleString('es-ES')}
                          disabled
                          className="w-full px-4 py-2.5 bg-brand-surface border border-brand-border rounded-xl text-sm text-brand-mid opacity-60 cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-semibold text-brand-mid mb-1">Última Actualización</label>
                        <input
                          type="text"
                          value={new Date(selectedCompany.fechaActualizacion).toLocaleString('es-ES')}
                          disabled
                          className="w-full px-4 py-2.5 bg-brand-surface border border-brand-border rounded-xl text-sm text-brand-mid opacity-60 cursor-not-allowed"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* La pestaña de flujo necesita h-full para que ReactFlow funcione */
                <div className="h-full">
                  <CompanyFlowBuilder
                    flowSteps={flowSteps}
                    onAddStep={handleAddStep}
                    onUpdateStep={handleUpdateStep}
                    onDeleteStep={handleDeleteStep}
                  />
                </div>
              )}
            </div>

            {/* Footer de acciones */}
            <div className="border-t border-brand-border px-4 md:px-8 py-4 flex gap-3 justify-end bg-brand-surface">
              <Button
                variant="secondary"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                loading={loading}
              >
                {loading ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-brand-mid">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white shadow-sm flex items-center justify-center">
                <svg className="w-8 h-8 text-brand-mid/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-brand-dark">Selecciona un agente</p>
              <p className="text-xs mt-1">para ver y editar sus detalles</p>
            </div>
          </div>
        )}
      </div>

      {/* Flow Builder Modal */}
      {selectedCompany && (
        <FlowBuilderModal
          isOpen={showFlowModal}
          flowSteps={flowSteps}
          companyName={selectedCompany.nombreEmpresa}
          onAddStep={handleAddStep}
          onUpdateStep={handleUpdateStep}
          onDeleteStep={handleDeleteStep}
          onClose={() => setShowFlowModal(false)}
        />
      )}

      {/* Knowledge Base Upload Modal */}
      {selectedCompany && (
        <KnowledgeBaseUploadModal
          isOpen={showKnowledgeBaseModal}
          onClose={() => setShowKnowledgeBaseModal(false)}
          companyId={selectedCompany.configId}
          companyName={selectedCompany.nombreEmpresa}
        />
      )}

      {/* Modal crear agente */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-brand-surface rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="sticky top-0 bg-brand-dark text-white px-6 py-5 rounded-t-2xl">
              <h2 className="text-lg font-bold">Crear Nuevo Agente</h2>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
              )}

              {[
                { label: 'Config ID *', field: 'configId', placeholder: 'empresa-abc' },
                { label: 'Nombre de la Empresa *', field: 'nombreEmpresa', placeholder: '' },
                { label: 'Phone Number ID *', field: 'phoneNumberId', placeholder: '' },
                { label: 'WSP Number ID *', field: 'wspNumberId', placeholder: '' },
                { label: 'Account ID *', field: 'accountId', placeholder: '' },
                { label: 'URL WebHook', field: 'urlWebHook', placeholder: '' },
              ].map(({ label, field, placeholder }) => (
                <div key={field}>
                  <label className="block text-xs font-semibold text-brand-dark mb-1.5">{label}</label>
                  <input
                    type="text"
                    value={(newCompany as any)[field] || ''}
                    onChange={(e) => setNewCompany({ ...newCompany, [field]: e.target.value })}
                    placeholder={placeholder}
                    className="w-full px-4 py-2.5 bg-brand-surface border border-brand-border rounded-xl text-sm text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                  />
                </div>
              ))}

              <div>
                <label className="block text-xs font-semibold text-brand-dark mb-1.5">Access Token *</label>
                <textarea
                  value={newCompany.accessToken || ''}
                  onChange={(e) => setNewCompany({ ...newCompany, accessToken: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2.5 bg-brand-surface border border-brand-border rounded-xl text-xs font-mono text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-brand-dark mb-1.5">Prompt del Agente</label>
                <textarea
                  value={newCompany.prompt || ''}
                  onChange={(e) => setNewCompany({ ...newCompany, prompt: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2.5 bg-brand-surface border border-brand-border rounded-xl text-sm text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                />
              </div>

              <div>
                <label className="flex items-center gap-2.5 cursor-pointer bg-brand-surface border border-brand-border px-4 py-2.5 rounded-xl w-full">
                  <input
                    type="checkbox"
                    checked={newCompany.agentActive || false}
                    onChange={(e) => setNewCompany({ ...newCompany, agentActive: e.target.checked })}
                    className="w-4 h-4 rounded accent-brand-dark"
                  />
                  <span className="text-sm font-semibold text-brand-dark">Agente Activo</span>
                </label>
              </div>

              <div>
                <label className="block text-xs font-semibold text-brand-dark mb-1.5">Estado</label>
                <CustomSelect
                  value={newCompany.estado || 'active'}
                  onChange={(val) => setNewCompany({ ...newCompany, estado: val as 'active' | 'inactive' })}
                  options={[
                    { value: 'active', label: 'Activa' },
                    { value: 'inactive', label: 'Inactiva' },
                  ]}
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-brand-light px-6 py-4 flex gap-3 justify-end rounded-b-2xl border-t border-gray-100">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowCreateModal(false);
                  setNewCompany({ agentActive: true, estado: 'active', prompt: '', urlWebHook: '' });
                  setError(null);
                }}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateCompany}
                loading={loading}
              >
                {loading ? 'Creando...' : 'Crear Agente'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompaniesPage;
